import type { Scene } from 'phaser';
import type { SightReadingFingeringReport } from 'Fingering/fingeringReport';
import type { HandStates } from 'Fingering/phraseFingering';
import { MidiEventState } from 'Types';
import type { TutorialInputUsed } from 'Types/EventTypes';
import { Timestamp } from 'Types/ExerciseData';
import type { PlaybackPhase } from 'Phaser/PlaybackMachine/playbackMachine.types';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';

export type StartPlayingArgs = {
  measureOffset: number;
  processEventsQueue: [() => void, unknown];
};

export type AdaptiveTempoState = {
  state: 'started' | 'completed' | 'progress';
  value: number;
  isBottomPhrase?: boolean;
};

export enum AppTourStepHelper {
  ExpandTempoControls = 'expand-tempo-controls',
  EndTour = 'end-tour',
}

export type ToReactEvents = {
  ObjectiveComplete: { jumpTo: Timestamp };
  /** All objectives passed. `inputUsed`: what the player played the pass
   *  on (absent from older senders: read as the scene's grading). */
  ExercisePassed: { inputUsed: TutorialInputUsed } | void;
  ExercisePaused: { lastPlayedMeasure?: number; pausedAtTimestamp?: number };
  ExerciseResumed: void;
  PhaserReady: void;
  CurrentSceneReady: Scene;
  ShowExercise: void;
  ObjectiveSkipped: {
    jumpTo: Timestamp;
    isLastObjective: boolean;
    /** What the player played the objective on, when the scene knows. */
    inputUsed?: TutorialInputUsed;
  };
  /** Web: the scene is loading the next objective of the same exercise and
   *  asks for the tutorial video's narration for it, from `from` up to the
   *  objective's own StartExercise cue at `to`. */
  PlayNarration: { from: Timestamp; to: Timestamp };
  HideExercise: void;
  SkipButtonRequest: void;
  /** Ungraded tutorial: the objective's own run is over. Run the ring around
   *  Next for `durationMs`; when it closes, take the Skip path. */
  NextRingStart: { durationMs: number };
  StartedPlaying: StartPlayingArgs | void;
  ShowToast: string;
  HideToast: void;
  SetPhrasesTillNextTier: { phrasesTillNextTier: number };
  SetPhraseEligible: { eligible: boolean };
  ShowFeedbackToast: { state: MidiEventState };
  RepertoireCompleted: void;
  RepertoirePhraseCompleted: { phraseIndex: number };
  /** Mobile: the user double-tapped the score after the piece finished. React
   *  runs the canonical soft-refresh restart (clears `repertoireEnded`, resets
   *  the scene, auto-starts the count-in) instead of resuming at the last
   *  note. Emitted from the scene's `handleScoreTap`. */
  RepertoireRestartRequested: void;
  /** Emitted after the initial transactional OSMD window is rendered, laid
   * out, and activated. `phraseCount` is the complete piece count while
   * `renderedPhraseCount` identifies the immediately usable lazy window. */
  RepertoireScoreReady: {
    loadId?: string;
    phraseCount: number;
    renderedPhraseCount?: number;
  };
  /** Requests a clean score-scene rebuild after the browser restores WebGL or
   * Phaser has to recreate its singleton game. */
  RepertoireRenderRecoveryRequested: {
    reason: 'webgl-context-restored' | 'game-restarted';
  };
  SetNotePosDummy: { x: number; y: number };
  SetAdaptiveTempoState: AdaptiveTempoState;
  SuggestLevelChange: { performance: 'good' | 'poor'; customGoal?: boolean };
  // MUS-472: a served tempo adjustment completed (analytics).
  TempoAdjustmentServed: { tempo: number; direction: 'up' | 'down'; goalMastery: boolean };
  // MUS-474: a chevron-manager transition (award / loss / eligibility crossing).
  ChevronTransition: {
    transition: 'award' | 'loss' | 'eligibility';
    accuracy?: number;
    phraseS3Id?: string;
    hand?: string;
    // How many more perfect phrases are needed to advance a tier.
    phrasesUntilNextTier?: number;
  };
  AdaptiveTempoRegionCreated: 'speed-up' | 'slow-down';
  UpdateTempo: { tempo: number };
  /** Legacy: emitted by NoteSequenceExercise when exercise ends. */
  ExerciseEnded: void;
  /** Legacy: emitted by NoteSequenceExercise on fail. */
  ExerciseFail: void;
  AppTourHelper: { step: AppTourStepHelper };
  SetLoopBounds: { startTimestamp: number; endTimestamp: number } | null;
  /** MUS-243: emitted by the scene's playback machine on every state
   *  transition. Container listens and mirrors into Redux `playbackPhase`.
   *  `phase` reuses the shared {@link PlaybackPhase} type so this contract
   *  stays in sync with the machine's state names. */
  PlaybackPhaseChanged: {
    phase: PlaybackPhase;
    isLoopingActive: boolean;
    hasEverPlayedInThisLoop: boolean;
  };
  /** `sequenceId` is a monotonic per-request id assigned by the PhraseManager.
   *  React echoes it back on the matching PhraseReady so the manager can restore
   *  request order for the initial fill (responses may resolve out of order). */
  FetchPhrase: {
    excludeFirstNoteRest: boolean;
    sequenceId: number;
    /** Where the phrase before the requested one leaves each hand, when known. */
    handStates?: HandStates;
    /** The transposition in force, when the lesson fingers its phrases. */
    transposition?: number;
  };
  /** The key of the phrase currently active in the sight-reading renderer. */
  SightReadingKeyChanged: { label: string | null };
  /** MUS-651: the sight-reading queue has drawn its first phrase (the queue was
   *  empty, now holds one), so the lesson has something on screen. */
  SightReadingFirstPhraseDrawn: void;
  /** Testing aid: the answer to ReportSightReadingFingering. Null when the
   *  scene does not finger its phrases or has none on screen. */
  SightReadingFingeringReported: SightReadingFingeringReport | null;
  /** Testing aid: the runtime has fingered a phrase (again, after a change of
   *  transposition) and this is how long it took. Only when fingering is timed. */
  SightReadingPhraseFingered: { milliseconds: number };
  /** Confirms that the live sight-reading renderer rebuilt its occupied queue. */
  SightReadingTransposeApplied: {
    semitones: number;
    modeConversion: ParallelModeConversion;
    rebuiltPhraseCount: number;
  };
  /** Perfect Phrase Counter: the user just completed the qualifying streak of
   *  perfect loops, so the looped section is "perfected". The container shows a
   *  bottom toast in response and records the section. */
  SectionPerfected: SectionPerfectedArgs;
  /** The loop is about to clear its notes for the next pass (a loop wrap, a
   *  bracket change, a restart, or play with the loop on). Emitted synchronously
   *  before the reset: the last moment the pass's notes can be recorded. */
  LoopPassEnding: void;
};

/** The perfected section: the loop range in piece-relative whole-note
 *  timestamps, the measures it starts and ends in (numbered from 1 at the start
 *  of the piece), the streak target reached and the tempo it was played at. */
export type SectionPerfectedArgs = {
  loopStartTimestamp: number;
  loopEndTimestamp: number;
  loopStartMeasure: number;
  loopEndMeasure: number;
  streakTarget: number;
  bpm: number;
};
